import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { getDocumentSummaryAction } from 'src/redux/actions/documentBundleActions';
import { DocumentSummary } from '../mock-data';

interface Filters {
  dateFrom: Date;
  dateTo: Date;
  payerType: string;
  payerName: string;
  disciplines: string[];
  docTypes: string[];
  selectedPatientIds: string[];
  dateScope: string;
  /** SQ-171: optional cohort narrowing. */
  therapistId?: string;
  therapistScope?: string;
  /** SQ-181: include documents under a standing billing hold. */
  includeHeldFromBilling?: boolean;
}

export function useDocumentSummary(filters: Filters) {
  const dispatch = useDispatch();
  const { response, loading } = useSelector((state: any) => state.getDocumentSummaryReducer);

  useEffect(() => {
    const params: Record<string, string | string[]> = {
      date_from: format(filters.dateFrom, 'yyyy-MM-dd'),
      date_to: format(filters.dateTo, 'yyyy-MM-dd'),
    };
    if (filters.payerType) params.payer_type = filters.payerType;
    if (filters.payerName) params.payer_name = filters.payerName;
    if (filters.disciplines.length > 0) params.discipline = filters.disciplines;
    if (filters.docTypes.length > 0) params.doc_type = filters.docTypes;
    if (filters.selectedPatientIds.length > 0) params.patient_uuid = filters.selectedPatientIds;
    if (filters.dateScope && filters.dateScope !== 'range') params.date_scope = filters.dateScope;
    //  The scope only travels with an id — alone it would be meaningless, and the server
    //  folds it away anyway.
    if (filters.therapistId) {
      params.therapist_id = filters.therapistId;
      if (filters.therapistScope) params.therapist_scope = filters.therapistScope;
    }
    //  Only when true. Sending include_held_from_billing=false on every request would
    //  change nothing server-side and make every URL in the logs harder to read.
    if (filters.includeHeldFromBilling) params.include_held_from_billing = 'true';

    dispatch(getDocumentSummaryAction(params));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.payerType,
    filters.payerName,
    filters.dateScope,
    JSON.stringify(filters.disciplines),
    JSON.stringify(filters.docTypes),
    JSON.stringify(filters.selectedPatientIds),
    filters.therapistId,
    filters.therapistScope,
    //  Ticking the box changes how many documents the bundle holds, so the number has to
    //  move with it. A count that does not respond to a filter is worse than no count.
    filters.includeHeldFromBilling,
  ]);

  const summary: DocumentSummary | null = response
    ? {
        document_count: response.documentCount ?? 0,
        patient_count: response.patientCount ?? 0,
        size_bytes: response.sizeBytes ?? 0,
        // SQ-172: the breakdown behind document_count, from the same query.
        groups: response.groups ?? [],
        // SQ-181: what the toggle beside this number would add.
        held_withheld_count: response.heldWithheldCount ?? 0,
      }
    : null;

  return { summary, loading };
}
